import React, { ComponentType } from 'react';
import ErrorBoundary from './components/ErrorBoundary';

/**
 * Gets the display name of an array of components.
 * @param components - Array of components.
 * @returns A string representing the display name.
 */

const getDisplayName = <T,>(components: ComponentType<T>[]) => {
  const componentNames = components.map((component) => {
    if (typeof window === 'undefined') {
      // Server-side: only 'name' is available
      return component?.name ?? 'Component';
    }
    // Client-side: 'displayName' or 'name' might be available
    return component?.displayName ?? component?.name ?? 'Component';
  });

  return componentNames.join(',');
};

/**
 * This function wraps a base component with one a more other components.
 * The other components will have access to the same props as the base component. And be wrapped in an ErrorBoundary.
 *
 * @param BaseComponent - The base component which will share its props with the other components.
 * @param TrackingComponents - Tracking components to be wrapped with the base component.
 * @returns A new component that wraps the base component with the other components.
 */

export const withTracking = <T extends object>(
  BaseComponent: ComponentType<T>,
  TrackingComponents: ComponentType<T> | ComponentType<T>[],
) => {
  const trackingComponentsArray = Array.isArray(TrackingComponents)
    ? TrackingComponents
    : [TrackingComponents];

  const WrapperComponent = (props: T) => (
    <>
      <BaseComponent {...props} />
      {trackingComponentsArray.map((Component, index) => (
        <ErrorBoundary key={index}>
          <Component key={index} {...props} />
        </ErrorBoundary>
      ))}
    </>
  );

  WrapperComponent.displayName = `withTracking(${getDisplayName([BaseComponent, ...trackingComponentsArray])})`;
  return WrapperComponent;
};
